import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  TextField,
  styled,
  Button as MuiButton,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { auth } from "../../../firebase.js";
import Logo from "../../../assets/images/Logo.png";
import {
  signInWithPhoneNumber,
  RecaptchaVerifier,
  linkWithCredential,
  signInWithCredential,
  PhoneAuthProvider,
} from "firebase/auth";
import Button from "../../../components/Button.js";
import Alert from "../../../components/Alert.js";
import { color } from "framer-motion";
import TextBox from "../../../components/TextField.js";
import Routes from "../../../utils/Routes.js";
import OtpInput from 'react-otp-input';

function PhoneNumberVerification({ className, handlePhoneVerified }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { verificationId, phone } = location.state || {};
  // const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otp, setOtp] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(300);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const otpRefs = useRef([]);

  useEffect(() => {
    otpRefs.current = otpRefs.current.slice(0, 6);
  }, []);

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to next box if filled
    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const timeOutCallback = useCallback(() => {
    setTimer((currTimer) => (currTimer > 0 ? currTimer - 1 : 0));
  }, []);

  // Resend OTP
  const resendOtpHandler = async () => {
    setTimer(300);
    setIsLoading(true);
    try {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, "sign-in-button", {
        size: "invisible",
      });

      const confirmation = await signInWithPhoneNumber(
        auth,
        `+1${phone}`,
        window.recaptchaVerifier
      );

      setAlert({
        open: true,
        message: "OTP Sent Successfully!",
        severity: "success",
      });
    } catch (error) {
      setAlert({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Submit OTP
  const otpSubmitHandler = async () => {
    if (otp.length !== 6) {
      setAlert({
        open: true,
        message: "Please enter a 6-digit OTP",
        severity: "warning",
      });
      return;
    }
    setIsLoading(true);
    try {
      const credential = PhoneAuthProvider.credential(
        verificationId,
        otp
      );

      const user = auth.currentUser;
      if (!user) {
        throw new Error("No authenticated user found.");
      }

      await linkWithCredential(user, credential);

      if (auth.currentUser.phoneNumber) {
        setAlert({
          open: true,
          message: "Phone number verified!",
          severity: "success",
        });
        handlePhoneVerified();

        navigate("/" + Routes.Home, { replace: true });
      }
    } catch (error) {
      setAlert({
        open: true,
        message: error.message,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (timer > 0) {
      const timeout = setTimeout(timeOutCallback, 1000);
      return () => clearTimeout(timeout);
    }
  }, [timer, timeOutCallback]);

  return (
    <div className={className}>
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 5 }}>
        {/* Snackbar Alert */}
        <Alert
          open={alert.open}
          message={alert.message}
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
        />

        {/* Logo */}
        <Box display="flex" flexDirection="column" alignItems="center">
          <img src={Logo} alt="Logo" className="logo" />
          <div className="heading">Enter One Time Password</div>
        </Box>

        {/* OTP Input */}

        <Box className="otpContainer">
          {/*           {otp.map((value, index) => (
            <TextBox
              key={index}
              className="otpInput"
              value={value}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputRef={(el) => (otpRefs.current[index] = el)}
              maxLength={1}
            />
          ))} */}

          <OtpInput
            className="otpContainer"
            value={otp}
            onChange={setOtp}
            numInputs={6}
            renderSeparator={<span> &nbsp;&nbsp;&nbsp;</span>}
            inputType="tel"
            shouldAutoFocus={true}
            renderInput={(props) => <input {...props} />}
            inputStyle="otpInput"
          />
        </Box>

        {/* Timer & Resend OTP */}
        <Box className="resendContainer">
          <Typography variant="body2">Didn't receive OTP?</Typography>
          {timer === 0 ? (
            <MuiButton
              variant="text"
              className="resendBtn"
              onClick={resendOtpHandler}
            >
              Resend
            </MuiButton>
          ) : (
            <Typography variant="body2" sx={{ color: "green" }}>
              {Math.floor(timer / 60)}:
              {timer % 60 < 10 ? `0${timer % 60}` : timer % 60}s
            </Typography>
          )}
        </Box>

        {/* Submit Button */}
        <button id="sign-in-button" style={{ display: "none" }}></button>
        <Button label="Submit" onClick={otpSubmitHandler} />
      </Container>
    </div>
  );
}

export default styled(PhoneNumberVerification)(() => ({
  ".logo": {
    width: "100px",
    height: "100px",
  },
  ".heading": {
    fontWeight: 600,
    fontSize: 22,
    letterSpacing: 0,
    textAlign: "center",
    color: "#234000",
  },
  ".otpContainer": {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginTop: "20px",
    marginBottom: "20px",
  },
  ".otpInput": {
    width: "35px !important",
    height: "50px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "bold",
    backgroundColor: "rgba(127, 168, 135, 0.2)",
    padding: "0px 0px !important",
    border: "none",
    outline: "none",
    outlineColor: "transparent",
  },
  ".resendContainer": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  ".resendBtn": {
    textTransform: "none",
    padding: 0,
    color: "#000000",
    fontWeight: 500,
  },
}));
